import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "./Login";

export default function VerifyEmail() {
    const [sp] = useSearchParams();
    const email = sp.get("email") || "";
    const [token, setToken] = useState(sp.get("token") || "");
    const [loading, setLoading] = useState(false);
    const [verified, setVerified] = useState(false);
    const { verify } = useAuth();
    const nav = useNavigate();

    const fetchDevToken = async () => {
        if (!email) return;
        try {
            const { data } = await api.get(`/auth/dev/last-token?email=${encodeURIComponent(email)}&purpose=verify`);
            setToken(data.token);
            toast.success("Token fetched from dev endpoint");
        } catch {
            toast.error("No unused token found");
        }
    };

    useEffect(() => {
        // Auto-verify if token was passed in URL
        if (sp.get("token") && !verified) {
            (async () => {
                try {
                    await verify(sp.get("token"));
                    setVerified(true);
                    toast.success("Email verified");
                } catch {
                    // stay on page for manual retry
                }
            })();
        }
        // eslint-disable-next-line
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await verify(token);
            setVerified(true);
            toast.success("Email verified");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed");
        } finally {
            setLoading(false);
        }
    };

    if (verified) {
        return (
            <AuthShell testid="page-verify-success" title="Email verified" sub="Setting up your workspace…">
                <button onClick={() => nav(sp.get("next") || "/app/onboarding")} className="w-full bg-navy border border-ice/50 text-arch-white px-6 py-3 text-sm hover:bg-navy/80 uppercase tracking-[0.15em]" data-testid="go-to-login">
                    Continue →
                </button>
            </AuthShell>
        );
    }

    return (
        <AuthShell testid="page-verify" title="Verify your email" sub={`Enter the verification token${email ? ` sent to ${email}` : ""}.`}>
            <form onSubmit={submit} className="space-y-5" data-testid="verify-form">
                <div>
                    <Label htmlFor="token">Verification token</Label>
                    <Input id="token" value={token} onChange={(e) => setToken(e.target.value)} required data-testid="verify-token" className="mt-2 font-mono text-xs" />
                </div>
                <div className="flex gap-2">
                    <button type="submit" disabled={loading} className="flex-1 bg-navy text-arch-white px-6 py-3 text-sm hover:opacity-90 disabled:opacity-50" data-testid="verify-submit">
                        {loading ? "Verifying…" : "Verify"}
                    </button>
                    <button type="button" onClick={fetchDevToken} className="border border-border px-4 py-3 text-sm hover:bg-secondary" data-testid="fetch-dev-token">
                        Fetch dev token
                    </button>
                </div>
            </form>
        </AuthShell>
    );
}
